<template>
  <Scroller class="securityScroller">
    <!-- Static sections -->
    <transition name="slide-fade" mode="out-in">
      <ScrollerSection id="intro" sticky="center" key="intro" v-if="!active">
        <div>
          <h1 class="f-title-5">10 guidelines for secure energy transitions</h1>
          <ScrollIcon>Keep scrolling to read more</ScrollIcon>
        </div>
      </ScrollerSection>
    </transition>
    <transition name="bounce" mode="out-in">
      <ScrollerSection
        v-if="active === 'investment'"
        id="bank"
        key="bank"
        sticky="right"
        behind
      >
        <img
          src="https://iea.imgix.net/179f650b-da92-4ca9-be34-d66cc43b3731/bank.svg"
          :style="{ height: '80%' }"
        />
      </ScrollerSection>
      <ScrollerSection
        v-if="active === 'cooperation'"
        id="sun"
        key="sun"
        sticky="right"
        behind
      >
        <img
          src="https://iea.imgix.net/179f650b-da92-4ca9-be34-d66cc43b3731/sun.svg"
          width="800"
        />
      </ScrollerSection>
      <ScrollerSection
        v-if="active === 'transition'"
        key="purpleSphere"
        id="purpleSphere"
        sticky="left"
        behind
      >
        <img
          src="https://iea.imgix.net/179f650b-da92-4ca9-be34-d66cc43b3731/purple_sphere.svg"
          :style="{ width: '100%' }"
        />
      </ScrollerSection>
      <ScrollerSection
        v-if="active === 'vulnerabilities'"
        id="networks"
        key="networks"
        sticky="right"
        behind
      >
        <img
          src="https://iea.imgix.net/179f650b-da92-4ca9-be34-d66cc43b3731/blue_network-left.svg"
          :style="{ height: '60%' }"
          class="network-pop"
        />
        <img
          src="https://iea.imgix.net/179f650b-da92-4ca9-be34-d66cc43b3731/blue_network-right.svg"
          :style="{ height: '60%' }"
        />
      </ScrollerSection>
    </transition>
    <!-- Normal sections -->
    <ScrollerSection id="investment" @enter="handleEnter" @leave="handleLeave">
      <LeftBlock
        title="Clean energy investment and energy efficiency are key to a secure exit from today's crisis"
      >
        <ol class="number-list" v-if="active === 'investment'">
          <li>
            <span class="round-number background-yellow">1</span> Synchronise
            scaling up a range of clean energy technologies with scaling back of
            fossil fuels
          </li>
          <li :style="{ animationDelay: '0.2s' }">
            <span class="round-number background-yellow">2</span> Tackle the
            demand side and prioritise energy efficiency
          </li>
        </ol>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="cooperation" @enter="handleEnter">
      <LeftBlock
        title="Global energy security cannot be achieved without everyone on board"
      >
        <ol class="number-list" v-if="active === 'cooperation'">
          <li>
            <span class="round-number background-green">3</span> Reverse the
            slide into energy poverty and give poor communities a lift into the
            new energy economy
          </li>
          <li :style="{ animationDelay: '0.2s' }">
            <span class="round-number background-green">4</span> Collaborate to
            bring down the cost of capital in emerging market and developing
            economies
          </li>
        </ol>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="transition" @enter="handleEnter">
      <Block>
        <h4 class="f-title-5">
          The transition away from oil and gas needs to be handled with care
        </h4>
        <ol class="number-list" v-if="active === 'transition'">
          <li>
            <span class="round-number background-purple">5</span> Manage the
            retirement and reuse of existing infrastructure carefully, some of
            it will be essential for a secure journey to net zero
          </li>
          <li :style="{ animationDelay: '0.2s' }">
            <span class="round-number background-purple">6</span> Tackle the
            specific risks facing producer economies
          </li>
        </ol>
      </Block>
    </ScrollerSection>
    <ScrollerSection id="vulnerabilities" @enter="handleEnter">
      <LeftBlock
        title="New vulnerabilities emerge as the world builds a new clean energy system"
      >
        <ol class="number-list" v-if="active === 'vulnerabilities'">
          <li>
            <span class="round-number background-blue">7</span> Invest in
            flexibility, a new watchword for electricity security
          </li>
          <li :style="{ animationDelay: '0.2s' }">
            <span class="round-number background-blue">8</span> Ensure diverse
            and resilient clean energy supply chains
          </li>
          <li :style="{ animationDelay: '0.4s' }">
            <span class="round-number background-blue">9</span> Foster the
            climate resilience of energy infrastructure
          </li>
        </ol>
      </LeftBlock>
    </ScrollerSection>
    <ScrollerSection id="governments" @enter="handleEnter">
      <Block
        title="Governments have to take the lead but cost-effective transitions also need well-functioning markets"
      >
        <ol class="number-list" v-if="active === 'governments'">
          <li>
            <span class="round-number background-red">10</span> Provide
            strategic direction and address market failures, but do not
            dismantle markets
          </li>
        </ol>
      </Block>
    </ScrollerSection>
    <img
      v-if="active"
      src="https://iea.imgix.net/3976b3df-746a-47a5-b242-d5df597d6076/rocket.svg"
      class="rocket"
      :class="{ 'rocket-away': active === 'governments' || active === 'fin' }"
    />
    <ScrollerSection id="fin" @enter="handleEnter" @leave="handleLeave" />
  </Scroller>
</template>

<script>
import {
  Scroller,
  ScrollerSection,
  ScrollIcon,
  Block,
} from "@international-energy-agency/vue-ui";
import LeftBlock from "../components/LeftBlock.vue";
import ActiveMixin from "../mixins/active";
export default {
  components: {
    ScrollerSection,
    ScrollIcon,
    Scroller,
    Block,
    LeftBlock,
  },
  mixins: [ActiveMixin],
};
</script>

<style>
@import url("../css/main.css");
.securityScroller .scroller__content .m-block--text {
  background: none !important;
}
@keyframes slide-up {
  0% {
    opacity: 0;
    top: 200vh;
    transform: scale(0);
  }
  100% {
    opacity: 1;
    top: 100vh;
    transform: scale(1);
  }
}
.network-pop {
  animation: bounce-in 0.5s;
  animation-fill-mode: backwards;
  animation-delay: 0.3s;
}
.rocket {
  position: absolute;
  top: 100vh;
  left: 28vw;
  height: 380vh;
  z-index: -2;
  transition: top 0.5s ease;
  animation: slide-up 1s;
  animation-fill-mode: backwards;
}
.rocket-away {
  top: -100vh;
  transition-delay: 1s;
}
ol.number-list li {
  margin-bottom: 1rem;
  min-height: 32px;
  animation: bounce-in 0.5s;
  animation-fill-mode: backwards;
}
.round-number {
  position: absolute;
  left: -48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  border-radius: 16px;
  width: 32px;
  height: 32px;
}
.round-number.background-purple,
.round-number.background-blue,
.round-number.background-red {
  color: #fff;
}
</style>
